import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Eye, Cloud, Languages as LangIcon, Bell, FileSpreadsheet,
    Sparkles, Check, ChevronDown, ChevronUp, ArrowRight,
    Star, TrendingUp, Timer, Quote,
} from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { fadeUp, Section, Kicker, MegaCTA } from "@/components/offer/parts";
import { useT } from "@/lib/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ Bilingual copy ============
const COPY = {
    en: {
        countdown: { label: "This month's Continuity cohort closes in", spots: "spots left of 10" },
        hero: {
            badge: "AI CONTENT GROWTH KIT · V2.4",
            h1a: "30 days of",
            h1b: "Instagram content",
            h1c: "for your cafe —",
            h1d: "in 48 hours.",
            sub: "Captions, hooks, AI visuals and reel scripts — curated by Kaily, generated with the latest AI engines. Built for Malaysian F&B.",
            ctaKit: "Buy a Kit · from RM 88",
            ctaCont: "Subscribe · from RM 68/mo",
            scroll: "Scroll · see how it works",
        },
        stats: [
            { n: "50+", l: "cafes served across KL & PJ" },
            { n: "12,000+", l: "AI images generated" },
            { n: "48h", l: "average delivery" },
            { n: "98%", l: "satisfaction · Kaily-reviewed" },
        ],
        problem: {
            kicker: "The 1 AM struggle",
            t: "Owning a cafe is 80 hours a week. Captions shouldn't take the other 8.",
            body: "Most owners spend Sunday nights staring at IG, hiring photographers at RM 800+ a shoot, and still posting late. There's a quieter way.",
        },
        beforeAfter: {
            kicker: "Before / After",
            t: "From inconsistent grid to a brand worth following.",
            before: "Before · DIY mixed bag",
            after: "After · slowcafe system",
        },
        founder: {
            kicker: "Founder · slowcafe.kaily",
            t: "Meet Kaily.",
            body: "Hi, I'm Kaily. I built slowcafe because I watched F&B owners burn out on social media. Every kit and every monthly drop is reviewed by hand — by me — before it lands in your inbox. No outsourced sweatshop. No automation slop.",
            sign: "— Kaily, Selangor",
        },
        inside: {
            kicker: "Inside every kit",
            t: "Not a stock pack. A 30-day publishing system.",
            items: [
                { n: "01", t: "30 Captions", d: "Hand-edited, brand-tuned, paste-ready for Publer." },
                { n: "02", t: "30 Hooks", d: "Scroll-stopping first lines, trend-tested weekly." },
                { n: "03", t: "30 AI Images", d: "Rendered by the latest AI image engine, styled to your DNA." },
                { n: "04", t: "8 Reel Scripts", d: "Shot-by-shot, narrated, with B-roll cues." },
                { n: "05", t: "30-Day Calendar", d: "Mon–Sun plan: Reels, Carousels, Stories." },
                { n: "06", t: "Saturday Shoot List", d: "Film a week in 90 minutes." },
            ],
        },
        showcase: { kicker: "The grid", t: "Real aesthetics. Real engagement." },
        tech: {
            kicker: "Always evolving",
            t: "System v2.4 · Refreshed monthly.",
            body: "Refreshed every month with new prompts, new trends, new visual styles. Subscribers get the upgrade automatically.",
            stack: ["AI Copywriting Engine", "AI Food Photography Engine", "AI Reel Video Engine", "Monthly Trend Radar", "Automated Delivery", "Secure Cloud Library"],
        },
        pricing: {
            kicker: "Three tiers · two ways to pay",
            t: "Pick your level. Buy it once, or stay on monthly.",
            sub: "Same content, same delivery — choose if you want it as a one-shot kit or as a fresh drop every month.",
            tiers: [
                {
                    id: "starter", kitId: "Starter",
                    name: "Starter", tagline: "Full text system · you shoot the photos",
                    bullets: [
                        "Full 30-day calendar · all captions + hooks",
                        "First 7 days of AI images — FREE",
                        "Days 8-30: shoot yourself with daily shot scripts",
                    ],
                    oneTime: "RM 88",
                    monthly: "RM 68",
                },
                {
                    id: "standard", kitId: "Pro", pop: true,
                    name: "Standard", tagline: "All images AI-generated · zero shooting",
                    bullets: [
                        "Full 30-day calendar · all captions + hooks",
                        "ALL 30 days of AI images — no shooting needed",
                        "No video needed — images carry the grid",
                        "Publer CSV auto-publish export",
                    ],
                    oneTime: "RM 248",
                    monthly: "RM 168",
                },
                {
                    id: "premium", kitId: "Premium", topTier: true,
                    name: "Premium", tagline: "Everything AI · images + reel videos",
                    bullets: [
                        "Full 30-day calendar · all captions + hooks",
                        "ALL AI images + 5 AI reel videos",
                        "Zero shooting — fully hands-off",
                        "Brand DNA tuning session",
                        "Priority WhatsApp line",
                    ],
                    oneTime: "RM 488",
                    monthly: "RM 348",
                },
            ],
            oneTimeLabel: "Pay once",
            oneTimeSub: "One-time purchase",
            monthlyLabel: "Or monthly",
            monthlySub: "Cancel anytime",
            oneTimeCta: "Buy this kit",
            monthlyCta: "Subscribe",
            popBadge: "Popular",
            topBadge: "All-In",
        },
        roi: {
            kicker: "The math",
            t: "≈ RM 4,800 of work. From RM 88.",
            lines: [
                ["Photographer · per shoot", "RM 800"],
                ["Copywriter · per month", "RM 500"],
                ["Reels editor · per month", "RM 300"],
                ["Trend research · per month", "RM 200"],
                ["Calendar planning · per month", "RM 300"],
                ["Brand DNA tuning · one-time", "RM 2,700"],
            ],
            total: "= RM 4,800",
        },
        perks: {
            kicker: "The details",
            t: "Small details, big difference.",
            items: [
                { i: Eye, t: "Reviewed by Kaily", d: "Every kit hand-checked before delivery." },
                { i: Cloud, t: "R2 Cloud Storage", d: "Persistent access to your media while your subscription is active." },
                { i: LangIcon, t: "Bilingual EN / CN", d: "Captions in both languages, publish-ready." },
                { i: Bell, t: "Daily Reminders", d: "Optional WhatsApp / SMS nudges." },
                { i: FileSpreadsheet, t: "Publer CSV Export", d: "Schedule a whole month in one upload." },
                { i: Sparkles, t: "Monthly Trend Refresh", d: "System updates as IG evolves." },
            ],
        },
        testi: {
            kicker: "Words from the community",
            items: [
                { q: "slowcafe gave me my weekends back. The captions feel like me, the images feel like the cafe.", a: "A. Lim", r: "Kopitiam owner · PJ" },
                { q: "We went from posting once a week to daily — without hiring anyone. Engagement doubled in a month.", a: "Sarah T.", r: "Matcha bar · Petaling Jaya" },
                { q: "The AI images are scary good. People DM us asking which photographer we use. I just smile.", a: "Marcus C.", r: "Specialty roastery · Bangsar" },
                { q: "I sleep on Sundays now. That alone is worth RM 168/mo.", a: "Yuki H.", r: "Japanese dessert café · Mont Kiara" },
            ],
        },
        faq: {
            kicker: "Questions",
            t: "Frequently asked.",
            items: [
                { q: "Are the AI images on-brand?", a: "Yes — we feed our system your brand DNA (palette, mood, menu, voice) before generating. Every image is reviewed by Kaily before delivery." },
                { q: "Do I own the content?", a: "Yes. Once delivered, it's 100% yours to publish, repurpose, edit." },
                { q: "What if I have no reference photos?", a: "We can run a single shoot day in Selangor / KL to seed the system, or use AI-generated reference frames." },
                { q: "Can I cancel Continuity anytime?", a: "Yes. Cancel from your billing portal — no contracts, no minimum commitment." },
                { q: "How fast is delivery?", a: "Kits ship in 48 hours after intake. Continuity drops on the 1st of every month." },
            ],
        },
        guarantee: "14-day refund on every Kit · Cancel Continuity anytime",
        final: {
            kicker: "One last thing",
            t: "Ready to slow down?",
            sub: "Stop spending Sundays writing captions. Start serving better coffee.",
            ctaKit: "Get the Growth Kit",
            ctaCont: "Subscribe to Continuity",
        },
    },
    cn: {
        countdown: { label: "本月 Continuity 名额倒计时", spots: "/ 10 个名额剩余" },
        hero: {
            badge: "AI 内容增长配套 · V2.4",
            h1a: "30 天的",
            h1b: "Instagram 内容,",
            h1c: "你的咖啡馆 —",
            h1d: "48 小时交付。",
            sub: "文案、钩子、AI 视觉、短视频脚本 — 由 Kaily 亲自审核,以最新 AI 引擎生成。专为马来西亚餐饮品牌打造。",
            ctaKit: "购买配套 · RM 88 起",
            ctaCont: "订阅 · RM 68/月 起",
            scroll: "滚动了解 · 它是如何运作的",
        },
        stats: [
            { n: "50+", l: "服务过的吉隆坡与 PJ 咖啡馆" },
            { n: "12,000+", l: "已生成 AI 图像" },
            { n: "48h", l: "平均交付时间" },
            { n: "98%", l: "满意度 · Kaily 亲审" },
        ],
        problem: {
            kicker: "凌晨 1 点的挣扎",
            t: "开咖啡馆已经一周 80 小时了。文案不该再吃掉剩下的 8 小时。",
            body: "大多数店主深夜盯着 IG 发呆,雇摄影师一次 RM 800 起,贴文还是迟到。有一种更安静的方式。",
        },
        beforeAfter: {
            kicker: "前 / 后对比",
            t: "从凌乱的 grid 到值得关注的品牌。",
            before: "之前 · DIY 杂乱",
            after: "之后 · slowcafe 系统",
        },
        founder: {
            kicker: "创办人 · slowcafe.kaily",
            t: "认识 Kaily。",
            body: "你好,我是 Kaily。我创立 slowcafe 是因为看到太多餐饮老板被社交媒体拖垮。每一份配套、每月的更新,都由我亲手审过才寄出。没有外包流水线,没有 AI 工业垃圾。",
            sign: "— Kaily,雪兰莪",
        },
        inside: {
            kicker: "配套内容",
            t: "不是图片包,是 30 天发布系统。",
            items: [
                { n: "01", t: "30 条文案", d: "亲手编辑、贴合品牌、可直接贴到 Publer。" },
                { n: "02", t: "30 个钩子", d: "针对 IG 趋势测试过的开头金句。" },
                { n: "03", t: "30 张 AI 图像", d: "最新 AI 图像引擎渲染,贴近品牌 DNA。" },
                { n: "04", t: "8 个短视频脚本", d: "逐镜拆解、有旁白、附 B-roll。" },
                { n: "05", t: "30 天日历", d: "周一到周日:Reels / 图文 / 限时全规划。" },
                { n: "06", t: "周六拍摄清单", d: "90 分钟拍完整周内容。" },
            ],
        },
        showcase: { kicker: "Grid 实例", t: "真实美感。真实互动。" },
        tech: {
            kicker: "持续进化",
            t: "系统 v2.4 · 每月刷新。",
            body: "每月更新 prompt、趋势与视觉风格。订阅用户自动获得升级。",
            stack: ["AI 文案引擎", "AI 美食摄影引擎", "AI Reel 视频引擎", "每月趋势雷达", "自动交付系统", "云端安全存储"],
        },
        pricing: {
            kicker: "3 个等级 · 2 种付款",
            t: "选你的等级,一次性买断或按月订阅。",
            sub: "同样的内容与交付速度 — 决定你要一次拿到所有,还是每月新鲜送达。",
            tiers: [
                {
                    id: "starter", kitId: "Starter",
                    name: "Starter", tagline: "完整文字系统 · 照片自己拍",
                    bullets: [
                        "完整 30 天日历 · 全部文案 + 钩子",
                        "免费附送首 7 天 AI 图",
                        "第 8-30 天：按每日拍摄脚本自己拍",
                    ],
                    oneTime: "RM 88",
                    monthly: "RM 68",
                },
                {
                    id: "standard", kitId: "Pro", pop: true,
                    name: "Standard", tagline: "图全部 AI 生成 · 完全不用拍",
                    bullets: [
                        "完整 30 天日历 · 全部文案 + 钩子",
                        "30 天的图全部 AI 生成 — 无需自己拍摄",
                        "无需视频 — 图片撑起整个版面",
                        "Publer CSV 自动发布导出",
                    ],
                    oneTime: "RM 248",
                    monthly: "RM 168",
                },
                {
                    id: "premium", kitId: "Premium", topTier: true,
                    name: "Premium", tagline: "全 AI 交付 · 图 + Reel 视频",
                    bullets: [
                        "完整 30 天日历 · 全部文案 + 钩子",
                        "全部 AI 图 + 5 条 AI Reel 视频",
                        "零拍摄 — 完全交给我们",
                        "品牌 DNA 调校面谈",
                        "WhatsApp 优先线",
                    ],
                    oneTime: "RM 488",
                    monthly: "RM 348",
                },
            ],
            oneTimeLabel: "一次性",
            oneTimeSub: "一次性购买",
            monthlyLabel: "或月度",
            monthlySub: "随时取消",
            oneTimeCta: "购买此套餐",
            monthlyCta: "月度订阅",
            popBadge: "热门",
            topBadge: "全包",
        },
        roi: {
            kicker: "价值解析",
            t: "≈ 价值 RM 4,800 · 仅 RM 88 起。",
            lines: [
                ["摄影师 · 每场拍摄", "RM 800"],
                ["文案 · 每月", "RM 500"],
                ["短视频剪辑 · 每月", "RM 300"],
                ["趋势研究 · 每月", "RM 200"],
                ["内容日历规划 · 每月", "RM 300"],
                ["品牌 DNA 调校 · 一次性", "RM 2,700"],
            ],
            total: "= RM 4,800",
        },
        perks: {
            kicker: "专属福利",
            t: "细节决定品质。",
            items: [
                { i: Eye, t: "Kaily 亲自审核", d: "每份配套交付前人工把关。" },
                { i: Cloud, t: "R2 云端存储", d: "订阅有效期内素材云端持续可用。" },
                { i: LangIcon, t: "中英双语", d: "文案双语提供,直接发布。" },
                { i: Bell, t: "每日提醒", d: "可选 WhatsApp / SMS 推送。" },
                { i: FileSpreadsheet, t: "Publer CSV 导出", d: "一次上传安排整月内容。" },
                { i: Sparkles, t: "每月趋势更新", d: "系统随 IG 趋势同步进化。" },
            ],
        },
        testi: {
            kicker: "来自社区的声音",
            items: [
                { q: "slowcafe 把我的周末还给我了。文案像我自己写的,图像就是咖啡馆的样子。", a: "A. Lim", r: "Kopitiam 店主 · PJ" },
                { q: "从每周一帖变成每天一帖 — 没多雇一个人。一个月内互动翻倍。", a: "Sarah T.", r: "抹茶吧 · 八打灵再也" },
                { q: "AI 图像好到吓人。客人来 DM 问我们用哪个摄影师。我只是微笑。", a: "Marcus C.", r: "精品烘焙 · Bangsar" },
                { q: "我周日终于能睡觉了。光这一点就值 RM 168/月。", a: "Yuki H.", r: "日式甜品店 · Mont Kiara" },
            ],
        },
        faq: {
            kicker: "常见问题",
            t: "你可能想问。",
            items: [
                { q: "AI 图像贴合我的品牌吗?", a: "贴合。我们会在生成前注入你的品牌 DNA(色调、氛围、菜单、语气)。每张图像都由 Kaily 审核后才交付。" },
                { q: "内容版权归谁?", a: "全部归你。一旦交付,你可以无限次发布、二次创作、修改。" },
                { q: "如果我没有参考照片?", a: "我们可以在 KL / 雪兰莪安排一次拍摄日,或使用 AI 生成的参考帧来启动系统。" },
                { q: "Continuity 可以随时取消吗?", a: "可以。在账单页面一键取消 — 无合约、无最低承诺。" },
                { q: "交付速度多快?", a: "提交意向后 48 小时内送达配套;Continuity 订阅每月 1 号自动交付。" },
            ],
        },
        guarantee: "配套享 14 天退款 · Continuity 随时可取消",
        final: {
            kicker: "最后一件事",
            t: "准备好放慢脚步了吗?",
            sub: "别再用周日写文案。把时间留给煮一杯更好的咖啡。",
            ctaKit: "购买配套",
            ctaCont: "订阅 Continuity",
        },
    },
};


// ============ Mega CTA buttons ============
// Big, dramatic, mobile-first buttons that anchor RM 4,800 → RM 88
const MEGA_LABELS = {
    en: {
        from: "Today only · from",
        save: "Save RM 4,712",
        buyKitNow: "Get your Growth Kit · now",
    },
    cn: {
        from: "限时特惠 · 仅需",
        save: "省 RM 4,712",
        buyKitNow: "立即获取增长配套",
        subNow: "立即订阅 · 随时取消",
        saveAlt: "比单独雇人省 RM 4,632",
    },
};

// ============ Mobile sticky bottom CTA bar ============
function MobileStickyCTA({ kitHref, contHref, lang }) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        const onScroll = () => setShow(window.scrollY > 600);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    const kitIsAnchor = typeof kitHref === "string" && kitHref.startsWith("#");
    const kitIsExternal = typeof kitHref === "string" && /^https?:\/\//i.test(kitHref);
    const kitProps = kitIsExternal
        ? { href: kitHref, target: "_blank", rel: "noopener noreferrer" }
        : kitIsAnchor ? { href: kitHref } : null;
    const KitTag = kitProps ? "a" : Link;
    const kitTagProps = kitProps || { to: kitHref };
    return (
        <div
            data-testid="offer-mobile-sticky-cta"
            className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-cafe-cream/95 backdrop-blur-md border-t border-cafe-espresso/15 shadow-[0_-10px_30px_-5px_rgba(0,0,0,0.08)] transition-transform duration-300 ${show ? "translate-y-0" : "translate-y-full"}`}
        >
            <div className="grid grid-cols-2 gap-2 p-2.5">
                <KitTag
                    {...kitTagProps}
                    data-testid="offer-sticky-cta-kit"
                    className="flex flex-col items-center justify-center py-3 px-2 rounded-full bg-cafe-espresso hover:bg-cafe-olive text-cafe-cream active:scale-[0.97] transition-all"
                >
                    <span className="font-serif text-xl leading-none tracking-[-0.015em]" style={{ fontWeight: 500 }}>RM 88</span>
                    <span className="text-[10px] tracking-[0.18em] uppercase mt-1 font-medium opacity-80">
                        {lang === "cn" ? "购买配套" : "Buy a Kit"}
                    </span>
                </KitTag>
                <Link
                    to={contHref}
                    data-testid="offer-sticky-cta-cont"
                    className="flex flex-col items-center justify-center py-3 px-2 rounded-full bg-cafe-cream border border-cafe-espresso text-cafe-espresso active:scale-[0.97] transition-transform"
                >
                    <span className="font-serif text-xl leading-none tracking-[-0.015em]" style={{ fontWeight: 500 }}>RM 68<span className="text-xs font-light opacity-60">/mo</span></span>
                    <span className="text-[10px] tracking-[0.18em] uppercase mt-1 font-medium opacity-60">
                        {lang === "cn" ? "月度订阅" : "Subscribe"}
                    </span>
                </Link>
            </div>
        </div>
    );
}

// ============ Countdown banner ============
function CountdownBanner({ c, lang }) {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    // End of current month, 23:59:59
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const diff = Math.max(0, end - now);
    const days = Math.floor(diff / 86400000);
    const hrs = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    // Pseudo-stable "spots left" — derived from date so it doesn't flicker but feels alive
    const spots = Math.max(2, 9 - (now.getDate() % 7));
    const pad = (n) => String(n).padStart(2, "0");
    return (
        <div className="bg-cafe-cream text-cafe-espresso/90 border-b border-cafe-espresso/10 py-3 px-6 text-[12px] md:text-[13px] tracking-wide" data-testid="offer-countdown-banner">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-3 md:gap-8">
                <div className="flex items-center gap-2">
                    <Timer className="h-3.5 w-3.5 text-cafe-olive" strokeWidth={1.6} />
                    <span className="uppercase tracking-[0.22em] text-cafe-espresso/60">{c.label}</span>
                </div>
                <div className="flex items-center gap-3 font-mono">
                    <span><span className="text-cafe-olive font-medium">{pad(days)}</span><span className="text-cafe-espresso/50 ml-1">{lang === "cn" ? "天" : "d"}</span></span>
                    <span><span className="text-cafe-olive font-medium">{pad(hrs)}</span><span className="text-cafe-espresso/50 ml-1">{lang === "cn" ? "时" : "h"}</span></span>
                    <span><span className="text-cafe-olive font-medium">{pad(mins)}</span><span className="text-cafe-espresso/50 ml-1">{lang === "cn" ? "分" : "m"}</span></span>
                    <span><span className="text-cafe-olive font-medium">{pad(secs)}</span><span className="text-cafe-espresso/50 ml-1">{lang === "cn" ? "秒" : "s"}</span></span>
                </div>
                <div className="hidden md:flex items-center gap-1.5">
                    <span className="text-cafe-olive font-medium">{spots}</span>
                    <span className="text-cafe-espresso/60">{c.spots}</span>
                </div>
            </div>
        </div>
    );
}

// ============ Hero ============
function HeroIGMockup({ images }) {
    // Render an IG profile mockup — 3x4 grid of AI/stock reference imagery.
    // This mockup illustrates WHAT THE PRODUCT GENERATES — it is explicitly
    // labelled "AI PRODUCT EXAMPLE" so viewers never confuse it with Kaily's
    // real portfolio (which lives on /work).
    const cells = images.slice(0, 12);
    while (cells.length < 12) cells.push(null);
    return (
        <div className="relative">
            {/* AI product disclosure badge sits ABOVE the mockup for clarity */}
            <div className="mb-4 flex justify-center md:justify-end">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.26em] text-cafe-espresso/70 bg-cafe-latte/80 border border-cafe-espresso/15 rounded-full px-3 py-1" data-testid="ai-product-example-badge">
                    <span className="h-1.5 w-1.5 rounded-full bg-cafe-olive" />
                    AI product example — not Kaily&apos;s portfolio
                </span>
            </div>
            <div className="relative mx-auto w-full max-w-[360px] md:max-w-[400px] rounded-[2.5rem] bg-cafe-latte border border-cafe-espresso/10 p-3 md:p-4">
                {/* Faux IG header */}
                <div className="flex items-center justify-between px-3 pb-3 pt-1 border-b border-cafe-espresso/10">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-full bg-cafe-olive" />
                        <div>
                            <div className="text-cafe-espresso text-[13px] font-medium tracking-tight">slowcafe.cohort</div>
                            <div className="text-cafe-espresso/50 text-[11px]">30 posts · 8 reels · live</div>
                        </div>
                    </div>
                    <div className="text-cafe-olive text-[11px] uppercase tracking-[0.18em]">Example</div>
                </div>
                {/* 3x4 image grid */}
                <div className="grid grid-cols-3 gap-1 mt-3">
                    {cells.map((src, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.15 + i * 0.04 }}
                            className="aspect-square bg-cafe-espresso/4 overflow-hidden"
                        >
                            {src && <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={(e) => { const fb = `https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&q=70&sig=${i}`; if (e.currentTarget.src !== fb) e.currentTarget.src = fb; }} className="w-full h-full object-cover" />}
                        </motion.div>
                    ))}
                </div>
                {/* Faux stats */}
                <div className="flex justify-between px-3 pt-3 mt-3 border-t border-cafe-espresso/10 text-[11px] text-cafe-espresso/60">
                    <span><span className="text-cafe-espresso font-medium">+184%</span> engagement</span>
                    <span><span className="text-cafe-espresso font-medium">2.4k</span> new followers</span>
                </div>
            </div>
        </div>
    );
}

function Hero({ c, lang, images, kitHref, contHref }) {
    return (
        <section
            id="offer-hero"
            className="relative overflow-hidden bg-cafe-cream text-cafe-espresso pt-20 md:pt-28 pb-20 md:pb-32"
            data-testid="offer-hero"
        >
            {/* Grain overlay */}
            <div
                className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay"
                style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%221.2%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.6%22/></svg>")' }}
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <motion.div {...fadeUp} className="lg:col-span-7">
                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-cafe-olive mb-8">
                        <span className="h-px w-8 bg-cafe-olive" />
                        {c.badge}
                    </div>
                    <h1 className="font-display tracking-[-0.035em] text-cafe-espresso text-[2.5rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] mb-8">
                        {c.h1a} <span className="italic text-cafe-olive">{c.h1b}</span>
                        <span className="block">{c.h1c}</span>
                        <span className="block font-light text-cafe-espresso">{c.h1d}</span>
                    </h1>
                    <p className="font-light text-cafe-espresso/70 text-base md:text-lg leading-relaxed max-w-xl mb-10">
                        {c.sub}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                        <MegaCTA
                            to={kitHref}
                            variant="primary"
                            lang={lang}
                            testid="offer-hero-cta-kit"
                            anchorOld="RM 4,800"
                            price="RM 88"
                            saveBadge={MEGA_LABELS[lang]?.save || MEGA_LABELS.en.save}
                            subline={lang === "cn" ? "立即获取增长配套" : "Get your Growth Kit · now"}
                        />
                        <MegaCTA
                            to={contHref}
                            variant="secondary"
                            lang={lang}
                            testid="offer-hero-cta-cont"
                            headline={lang === "cn" ? "或按月订阅" : "Or subscribe monthly"}
                            price="RM 68"
                            suffix={lang === "cn" ? "/月" : "/mo"}
                            subline={lang === "cn" ? "随时取消" : "Cancel anytime"}
                        />
                    </div>
                    <div className="mt-12 flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-cafe-espresso/50">
                        <div className="h-px w-6 bg-cafe-cream/30" />
                        {c.scroll}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5"
                >
                    <HeroIGMockup images={images} />
                </motion.div>
            </div>
        </section>
    );
}

// ============ Stats bar ============
function StatsBar({ stats }) {
    return (
        <div className="bg-cafe-latte border-b border-cafe-espresso/10 py-12 md:py-16 px-6 md:px-12">
            <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
                {stats.map((s, i) => (
                    <motion.div
                        key={i}
                        {...fadeUp}
                        transition={{ duration: 0.6, delay: i * 0.08 }}
                        className="text-center md:text-left"
                        data-testid={`offer-stat-${i}`}
                    >
                        <div className="font-display text-cafe-olive text-4xl md:text-6xl tracking-[-0.03em] mb-2">{s.n}</div>
                        <div className="text-cafe-espresso/60 text-[12px] md:text-sm tracking-wide leading-snug">{s.l}</div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

// ============ Problem ============
function Problem({ c }) {
    return (
        <Section>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] mb-8 tracking-[-0.03em]">
                    {c.t}
                </h2>
                <p className="font-light text-cafe-mocha text-lg md:text-xl leading-relaxed max-w-3xl">
                    {c.body}
                </p>
            </motion.div>
        </Section>
    );
}

// ============ Before / After ============
function BeforeAfter({ c, images }) {
    const beforeImgs = [
        "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=400&q=70",
        "https://images.unsplash.com/photo-1517232115160-ff93364542dd?w=400&q=70",
        "https://images.unsplash.com/photo-1453614512568-c4024d13c247?w=400&q=70",
        "https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=400&q=70",
        "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=400&q=70",
        "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=400&q=70",
    ];
    const afterImgs = images.slice(0, 6);
    while (afterImgs.length < 6) afterImgs.push(beforeImgs[afterImgs.length]);
    return (
        <Section dark>
            <motion.div {...fadeUp} className="mb-14 max-w-3xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.03em]">
                    {c.t}
                </h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                {[{ title: c.before, list: beforeImgs, mono: true }, { title: c.after, list: afterImgs, mono: false }].map((col, idx) => (
                    <motion.div key={idx} {...fadeUp} transition={{ duration: 0.7, delay: idx * 0.15 }}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-cafe-espresso/50">{col.title}</div>
                            {!col.mono && <Star className="h-4 w-4 text-cafe-olive" strokeWidth={1.6} fill="#D4A574" />}
                        </div>
                        <div className="grid grid-cols-3 gap-1.5 md:gap-2">
                            {col.list.map((src, i) => (
                                <div key={i} className="aspect-square bg-cafe-espresso/4 overflow-hidden">
                                    <img
                                        src={src}
                                        alt=""
                                        loading="lazy"
                                        className={`w-full h-full object-cover transition-all ${col.mono ? "grayscale opacity-60" : ""}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

// ============ Founder ============
function FounderStory({ c }) {
    return (
        <Section>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
                <motion.div {...fadeUp} className="md:col-span-5">
                    <div className="aspect-[4/5] bg-[#F0E8DD] border border-cafe-espresso/10 overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1635847420403-d03e037078a0?w=800&q=80"
                            alt="Kaily portrait"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </motion.div>
                <motion.div {...fadeUp} className="md:col-span-7 md:pt-12">
                    <Kicker>{c.kicker}</Kicker>
                    <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.08] tracking-[-0.03em] mb-8">
                        {c.t}
                    </h2>
                    <p className="font-light text-cafe-mocha text-lg leading-relaxed mb-6 whitespace-pre-line">
                        {c.body}
                    </p>
                    <p className="font-serif italic text-cafe-olive text-lg">{c.sign}</p>
                </motion.div>
            </div>
        </Section>
    );
}

// ============ What's Inside ============
function WhatsInside({ c }) {
    return (
        <Section dark>
            <motion.div {...fadeUp} className="mb-16 md:mb-20 max-w-3xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.03em]">
                    {c.t}
                </h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-cafe-espresso/8">
                {c.items.map((it, i) => (
                    <motion.div
                        key={it.n}
                        {...fadeUp}
                        transition={{ duration: 0.6, delay: i * 0.05 }}
                        className="bg-cafe-cream p-10 md:p-12 hover:bg-[#221610] transition-colors"
                        data-testid={`offer-inside-${it.n}`}
                    >
                        <div className="font-serif text-cafe-olive text-2xl mb-6">{it.n}</div>
                        <div className="font-serif text-cafe-espresso text-2xl md:text-3xl mb-3 tracking-tight">{it.t}</div>
                        <p className="font-light text-cafe-espresso/60 leading-relaxed">{it.d}</p>
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

// ============ Showcase ============
function Showcase({ c, images }) {
    return (
        <Section>
            <motion.div {...fadeUp} className="mb-14 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]">
                    {c.t}
                </h2>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {images.slice(0, 12).map((src, i) => (
                    <motion.div
                        key={i}
                        {...fadeUp}
                        transition={{ duration: 0.55, delay: i * 0.03 }}
                        className={`overflow-hidden bg-[#F0E8DD] ${i % 5 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-square"}`}
                    >
                        <img src={src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

// ============ Tech ============
function TechStack({ c }) {
    return (
        <Section dark>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.03em] mb-8">
                    {c.t}
                </h2>
                <p className="font-light text-cafe-espresso/70 text-lg leading-relaxed mb-10 max-w-2xl">{c.body}</p>
                <div className="flex flex-wrap gap-3">
                    {c.stack.map((s) => (
                        <span
                            key={s}
                            className="border border-cafe-espresso/15 text-cafe-espresso/85 text-sm px-5 py-2.5 rounded-full font-light hover:border-cafe-olive hover:text-cafe-olive transition-colors"
                        >
                            {s}
                        </span>
                    ))}
                </div>
            </motion.div>
        </Section>
    );
}

// ============ Pricing ============
// Stripe one-time Kit checkout — POST to /api/checkout/kit and redirect.
// Replaces the legacy WhatsApp fallback once kit checkout was added (Stripe Live).
const KIT_TIER_TO_ID = {
    starter: "kit-starter",
    pro: "kit-pro",
    premium: "kit-premium",
};

async function startKitCheckout(tierName, lang) {
    const tierId = KIT_TIER_TO_ID[tierName.toLowerCase()];
    if (!tierId) {
        // Should never happen — defensive guard.
        window.location.hash = "#offer-pricing";
        return;
    }
    try {
        const r = await axios.post(`${API}/checkout/kit`, {
            tier: tierId,
            origin_url: window.location.origin,
        });
        if (r.data?.checkout_url) {
            window.location.href = r.data.checkout_url;
            return;
        }
        throw new Error("No checkout_url in response");
    } catch (e) {
        const msg = e?.response?.data?.detail || (lang === "cn" ? "暂时无法启动付款,请稍后再试" : "Couldn't start checkout — please try again");
        if (typeof window !== "undefined") window.alert(msg);
    }
}

// Map the unified Pricing tier key (Starter / Standard / Premium) to the
// Continuity subscription tier IDs the backend recognises.
const SUB_TIER_TO_ID = {
    starter: "continuity-68",
    standard: "continuity-168",
    premium: "continuity-348",
};

async function startSubscriptionCheckout(tierName, lang) {
    const tierId = SUB_TIER_TO_ID[tierName.toLowerCase()];
    if (!tierId) {
        window.location.href = lang === "cn" ? "/subscribe/cn" : "/subscribe";
        return;
    }
    try {
        const r = await axios.post(`${API}/subscriptions/checkout`, {
            tier: tierId,
            origin_url: window.location.origin,
            // email omitted — Stripe collects it on its hosted page (parity with Kit checkout)
        });
        if (r.data?.checkout_url) {
            window.location.href = r.data.checkout_url;
            return;
        }
        throw new Error("No checkout_url in response");
    } catch (e) {
        const msg = e?.response?.data?.detail || (lang === "cn" ? "暂时无法启动订阅,请稍后再试" : "Couldn't start subscription — please try again");
        if (typeof window !== "undefined") window.alert(msg);
    }
}


// ============ Pricing (unified 3-tier · 2-price layout) ============
function Pricing({ c, contHref, lang }) {
    return (
        <Section id="offer-pricing">
            <motion.div {...fadeUp} className="mb-12 md:mb-16 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em] mb-5">
                    {c.t}
                </h2>
                {c.sub && (
                    <p className="font-light text-cafe-mocha text-base md:text-lg max-w-2xl leading-relaxed">{c.sub}</p>
                )}
            </motion.div>

            <div className="space-y-5 md:space-y-6">
                {c.tiers.map((t, idx) => {
                    const cardBg = t.pop
                        ? "bg-[#F0E8DD]"
                        : t.topTier
                        ? "bg-cafe-cream border-l-4 border-cafe-olive"
                        : "bg-cafe-cream";
                    return (
                        <motion.div
                            key={t.id}
                            {...fadeUp}
                            transition={{ duration: 0.55, delay: idx * 0.08 }}
                            data-testid={`offer-tier-${t.id}`}
                            className={`${cardBg} border border-cafe-espresso/12 rounded-2xl p-6 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10`}
                        >
                            {/* Left: name + bullets */}
                            <div className="md:col-span-7">
                                <div className="flex items-center gap-3 mb-3 flex-wrap">
                                    <h3 className="font-display text-3xl md:text-4xl text-cafe-espresso tracking-[-0.03em]">{t.name}</h3>
                                    {t.pop && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-medium">
                                            <Star className="h-3 w-3 fill-cafe-olive" strokeWidth={0} /> {c.popBadge}
                                        </span>
                                    )}
                                    {t.topTier && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-cafe-olive text-cafe-espresso font-medium px-2.5 py-1 rounded-full">
                                            {c.topBadge}
                                        </span>
                                    )}
                                </div>
                                <p className="font-light text-cafe-mocha text-sm md:text-base mb-5 leading-relaxed">{t.tagline}</p>
                                <ul className="space-y-2">
                                    {t.bullets.map((b, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm md:text-[15px] text-cafe-espresso/85 leading-relaxed">
                                            <Check className="h-4 w-4 text-cafe-olive flex-shrink-0 mt-0.5" strokeWidth={2} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Right: two prices */}
                            <div className="md:col-span-5 grid grid-cols-2 gap-3 md:gap-4 self-center">
                                {/* One-time */}
                                <button
                                    type="button"
                                    onClick={() => startKitCheckout(t.kitId, lang)}
                                    data-testid={`offer-tier-${t.id}-onetime`}
                                    className="group relative overflow-hidden flex flex-col items-center justify-center text-center p-5 md:p-6 rounded-xl bg-gradient-to-br from-[#E8B97A] via-[#D4A574] to-[#B88A55] hover:from-[#F0C384] hover:to-[#C39562] text-[#1A0F0A] shadow-[0_15px_40px_-12px_rgba(212,165,116,0.5)] hover:-translate-y-0.5 transition-all"
                                >
                                    <span className="text-[10px] tracking-[0.24em] uppercase font-medium text-[#1A0F0A]/65 mb-2">{c.oneTimeLabel}</span>
                                    <span className="font-display text-3xl md:text-4xl tracking-[-0.03em] leading-none">{t.oneTime}</span>
                                    <span className="text-[11px] text-[#1A0F0A]/65 mt-2 font-light">{c.oneTimeSub}</span>
                                    <span className="mt-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em]">
                                        {c.oneTimeCta} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2.2} />
                                    </span>
                                </button>
                                {/* Monthly */}
                                <button
                                    type="button"
                                    onClick={() => startSubscriptionCheckout(t.id, lang)}
                                    data-testid={`offer-tier-${t.id}-monthly`}
                                    className="group flex flex-col items-center justify-center text-center p-5 md:p-6 rounded-xl bg-cafe-cream hover:bg-[#221610] text-cafe-espresso border-2 border-cafe-cream/15 hover:border-cafe-olive hover:-translate-y-0.5 transition-all"
                                >
                                    <span className="text-[10px] tracking-[0.24em] uppercase font-medium text-cafe-olive mb-2">{c.monthlyLabel}</span>
                                    <span className="font-display text-3xl md:text-4xl tracking-[-0.03em] leading-none">
                                        {t.monthly}<span className="text-base text-cafe-espresso/60 font-light ml-1">{lang === "cn" ? "/月" : "/mo"}</span>
                                    </span>
                                    <span className="text-[11px] text-cafe-espresso/60 mt-2 font-light">{c.monthlySub}</span>
                                    <span className="mt-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em]">
                                        {c.monthlyCta} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2.2} />
                                    </span>
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </Section>
    );
}


// ============ ROI ============
function ROI({ c }) {
    return (
        <Section dark>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.03em] mb-12">
                    {c.t}
                </h2>
                <div className="space-y-px bg-cafe-espresso/8 border border-cafe-espresso/10 mb-10">
                    {c.lines.map((line, i) => (
                        <motion.div
                            key={i}
                            {...fadeUp}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                            className="flex items-baseline justify-between bg-cafe-cream p-5 md:p-6"
                        >
                            <span className="font-light text-cafe-espresso/75 text-sm md:text-base">{line[0]}</span>
                            <span className="font-serif text-xl md:text-2xl text-cafe-espresso/90 line-through decoration-cafe-cream/30">{line[1]}</span>
                        </motion.div>
                    ))}
                </div>
                <div className="flex items-baseline justify-end gap-4 pt-4 border-t border-cafe-olive/40">
                    <span className="text-cafe-olive/70 text-sm uppercase tracking-[0.22em]">Total</span>
                    <span className="font-display text-4xl md:text-6xl text-cafe-olive tracking-[-0.03em]">{c.total}</span>
                </div>
            </motion.div>
        </Section>
    );
}

// ============ Perks ============
function Perks({ c }) {
    return (
        <Section>
            <motion.div {...fadeUp} className="mb-16 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]">
                    {c.t}
                </h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                {c.items.map((it) => {
                    const Icon = it.i;
                    return (
                        <motion.div key={it.t} {...fadeUp}>
                            <div className="h-12 w-12 mb-5 rounded-full bg-cafe-olive/10 flex items-center justify-center">
                                <Icon className="h-5 w-5 text-cafe-olive" strokeWidth={1.5} />
                            </div>
                            <div className="font-serif text-xl text-cafe-espresso mb-2">{it.t}</div>
                            <p className="font-light text-cafe-mocha leading-relaxed">{it.d}</p>
                        </motion.div>
                    );
                })}
            </div>
        </Section>
    );
}

// ============ Testimonials Carousel ============
function Testimonials({ c }) {
    const [i, setI] = useState(0);
    const len = c.items.length;
    useEffect(() => {
        const t = setInterval(() => setI((v) => (v + 1) % len), 5500);
        return () => clearInterval(t);
    }, [len]);
    return (
        <Section dark>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker dark>{c.kicker}</Kicker>
                <div className="relative h-[280px] md:h-[260px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute inset-0"
                        >
                            <Quote className="h-10 w-10 text-cafe-olive/40 mb-6" strokeWidth={1.2} />
                            <p className="font-display text-2xl md:text-4xl text-cafe-espresso leading-snug italic mb-8 tracking-[-0.02em]">
                                {c.items[i].q}
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#D4A574] to-cafe-terracotta flex items-center justify-center font-serif text-[#1A0F0A] text-lg">
                                    {c.items[i].a.charAt(0)}
                                </div>
                                <div>
                                    <div className="text-cafe-espresso font-medium">{c.items[i].a}</div>
                                    <div className="text-cafe-espresso/60 text-sm">{c.items[i].r}</div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
                <div className="flex gap-2 mt-10">
                    {c.items.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setI(idx)}
                            data-testid={`offer-testi-dot-${idx}`}
                            aria-label={`Go to testimonial ${idx + 1}`}
                            className={`h-1 transition-all rounded-full ${i === idx ? "w-10 bg-cafe-olive" : "w-5 bg-cafe-cream/20 hover:bg-cafe-cream/40"}`}
                        />
                    ))}
                </div>
            </motion.div>
        </Section>
    );
}

// ============ FAQ ============
function FAQ({ c }) {
    const [open, setOpen] = useState(0);
    return (
        <Section>
            <motion.div {...fadeUp} className="mb-12 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]">
                    {c.t}
                </h2>
            </motion.div>
            <div className="max-w-3xl">
                {c.items.map((it, i) => {
                    const isOpen = open === i;
                    return (
                        <div key={i} className="border-b border-cafe-espresso/15">
                            <button
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                data-testid={`offer-faq-${i}`}
                                className="w-full flex items-center justify-between text-left py-6 group"
                            >
                                <span className="font-serif text-lg md:text-xl text-cafe-espresso pr-6">{it.q}</span>
                                {isOpen
                                    ? <ChevronUp className="h-5 w-5 text-cafe-olive flex-shrink-0" strokeWidth={1.5} />
                                    : <ChevronDown className="h-5 w-5 text-cafe-mocha flex-shrink-0 group-hover:text-cafe-olive transition-colors" strokeWidth={1.5} />
                                }
                            </button>
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <p className="font-light text-cafe-mocha leading-relaxed pb-6 pr-10">{it.a}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </Section>
    );
}

// ============ Guarantee + Final ============
function Guarantee({ c }) {
    return (
        <div className="bg-cafe-olive text-cafe-espresso py-5 px-6 md:px-12" data-testid="offer-guarantee">
            <motion.div
                {...fadeUp}
                className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm md:text-base font-light tracking-wide text-center"
            >
                <Check className="h-4 w-4" strokeWidth={2} />
                {c}
            </motion.div>
        </div>
    );
}

function FinalCTA({ c, kitHref, contHref, lang }) {
    return (
        <section className="bg-cafe-cream text-cafe-espresso py-28 md:py-40 px-6 md:px-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,165,116,0.10),_transparent_60%)] pointer-events-none" />
            <motion.div {...fadeUp} className="relative max-w-3xl mx-auto">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-cafe-espresso leading-[1.05] tracking-[-0.035em] mb-8">
                    {c.t}
                </h2>
                <p className="font-light text-cafe-espresso/65 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
                    {c.sub}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center">
                    <MegaCTA
                        to={kitHref}
                        variant="primary"
                        lang={lang}
                        testid="offer-final-cta-kit"
                        anchorOld="RM 4,800"
                        price="RM 88"
                        saveBadge={MEGA_LABELS[lang]?.save || MEGA_LABELS.en.save}
                        subline={c.ctaKit}
                    />
                    <MegaCTA
                        to={contHref}
                        variant="secondary"
                        lang={lang}
                        testid="offer-final-cta-cont"
                        headline={lang === "cn" ? "或按月订阅" : "Or subscribe monthly"}
                        price="RM 68"
                        suffix={lang === "cn" ? "/月" : "/mo"}
                        subline={c.ctaCont}
                    />
                </div>
            </motion.div>
        </section>
    );
}

// ============ Page ============

/**
 * Deep-merges Studio overrides into the hardcoded COPY object.
 * - Strings: override if non-empty
 * - Arrays (inside_items, faq_items, stats): only override if the admin
 *   actually added ≥1 entry — otherwise keep the hardcoded list intact.
 * - Maps DB shape ({ t_en, d_en, t_cn, d_cn }) → page shape ({ t, d })
 */
function mergeKitContent(base, override, lang) {
    if (!override) return base;
    const isCn = lang === "cn";
    const pick = (en, cn) => (isCn ? cn || en : en || cn);
    const merged = { ...base };

    // Hero
    if (override.hero_badge_en || override.hero_badge_cn) {
        merged.hero = { ...base.hero, badge: pick(override.hero_badge_en, override.hero_badge_cn) || base.hero?.badge };
    }
    if (override.hero_sub_en || override.hero_sub_cn) {
        merged.hero = { ...merged.hero, sub: pick(override.hero_sub_en, override.hero_sub_cn) || base.hero?.sub };
    }

    // Stats
    if (Array.isArray(override.stats) && override.stats.length > 0) {
        merged.stats = override.stats.map((s) => ({ n: s.n || "", l: pick(s.l_en, s.l_cn) || "" }));
    }

    // Problem
    if (override.problem_title_en || override.problem_title_cn || override.problem_body_en || override.problem_body_cn) {
        merged.problem = {
            ...base.problem,
            t: pick(override.problem_title_en, override.problem_title_cn) || base.problem?.t,
            body: pick(override.problem_body_en, override.problem_body_cn) || base.problem?.body,
        };
    }

    // Founder
    if (override.founder_body_en || override.founder_body_cn) {
        merged.founder = {
            ...base.founder,
            body: pick(override.founder_body_en, override.founder_body_cn) || base.founder?.body,
            sign: pick(override.founder_sign_en, override.founder_sign_cn) || base.founder?.sign,
        };
    }

    // Inside-the-kit
    if (override.inside_title_en || override.inside_title_cn || (Array.isArray(override.inside_items) && override.inside_items.length > 0)) {
        merged.inside = {
            ...base.inside,
            t: pick(override.inside_title_en, override.inside_title_cn) || base.inside?.t,
            items: (Array.isArray(override.inside_items) && override.inside_items.length > 0)
                ? override.inside_items.map((it) => ({
                    n: it.n || "",
                    t: pick(it.t_en, it.t_cn) || "",
                    d: pick(it.d_en, it.d_cn) || "",
                }))
                : base.inside?.items,
        };
    }

    // Guarantee
    if (override.guarantee_en || override.guarantee_cn) {
        merged.guarantee = pick(override.guarantee_en, override.guarantee_cn) || base.guarantee;
    }

    // FAQ
    if (Array.isArray(override.faq_items) && override.faq_items.length > 0) {
        merged.faq = {
            ...base.faq,
            items: override.faq_items.map((f) => ({
                q: pick(f.q_en, f.q_cn) || "",
                a: pick(f.a_en, f.a_cn) || "",
            })),
        };
    }

    // Final CTA
    if (override.final_title_en || override.final_title_cn || override.final_sub_en || override.final_sub_cn || override.final_cta_kit_en || override.final_cta_kit_cn) {
        merged.final = {
            ...base.final,
            t: pick(override.final_title_en, override.final_title_cn) || base.final?.t,
            sub: pick(override.final_sub_en, override.final_sub_cn) || base.final?.sub,
            ctaKit: pick(override.final_cta_kit_en, override.final_cta_kit_cn) || base.final?.ctaKit,
        };
    }

    return merged;
}

export default function Offer() {
    const { lang } = useT();
    const baseCopy = COPY[lang] || COPY.en;
    const [c, setC] = useState(baseCopy);
    const [images, setImages] = useState([]);
    // Phase 6c — Stripe mode banner. Payments can be unavailable in Preview
    // when the test key is not yet set. Fetch the honest status and surface a
    // banner so the "Buy a Kit" CTA doesn't disappoint silently.
    const [stripeMode, setStripeMode] = useState({ mode: null, payments_available: true });
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/subscriptions/mode`).then((r) => {
            if (!cancelled) setStripeMode(r.data || {});
        }).catch(() => { /* non-fatal */ });
        return () => { cancelled = true; };
    }, []);

    // Pull editable overrides from /api/kit-content and merge into the hardcoded
    // copy. Empty fields in the override doc fall back to the hardcoded version
    // so the page is ALWAYS complete even if Mongo is unreachable.
    useEffect(() => {
        let cancelled = false;
        setC(baseCopy); // reset to base when lang flips before fetch completes
        (async () => {
            try {
                const r = await axios.get(`${API}/kit-content`, {
                    params: { _t: Date.now() },
                    timeout: 8000,
                    headers: { "Cache-Control": "no-cache" },
                });
                if (cancelled) return;
                const merged = mergeKitContent(baseCopy, r.data || {}, lang);
                setC(merged);
            } catch {
                if (!cancelled) setC(baseCopy);
            }
        })();
        return () => { cancelled = true; };
    }, [lang, baseCopy]);

    // Mega "Buy a Kit" buttons in hero/final/sticky-mobile scroll to the pricing section
    // where users see all 3 Kit tiers + Continuity options. Tier rows then POST to
    // /api/checkout/kit and redirect to Stripe one-time payment.
    const kitHref = "#offer-pricing";
    const contHref = lang === "cn" ? "/subscribe/cn" : "/subscribe";

    useEffect(() => {
        let cancelled = false;
        const fallback = [
            "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&q=80",
            "https://images.unsplash.com/photo-1521017432531-fbd92d768814?w=600&q=80",
            "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=600&q=80",
            "https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=600&q=80",
            "https://images.unsplash.com/photo-1453614512568-c4024d13c247?w=600&q=80",
            "https://images.unsplash.com/photo-1493857671505-72967e2e2760?w=600&q=80",
            "https://images.unsplash.com/photo-1518737743670-6d35dbe5db9c?w=600&q=80",
            "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=600&q=80",
            "https://images.unsplash.com/photo-1525480122447-64809d765a44?w=600&q=80",
            "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=600&q=80",
            "https://images.unsplash.com/photo-1511920170033-f8396924c348?w=600&q=80",
            "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&q=80",
            "https://images.unsplash.com/photo-1517701604599-bb29b565090c?w=600&q=80",
            "https://images.unsplash.com/photo-1551024601-bec78aea704b?w=600&q=80",
            "https://images.unsplash.com/photo-1610632380989-680fe40816c6?w=600&q=80",
            "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&q=80",
        ];
        (async () => {
            try {
                const r = await axios.get(`${API}/gallery`, { params: { limit: 24 } });
                const items = Array.isArray(r.data) ? r.data : r.data?.items || [];
                const urls = items.map((it) => it.url || it.image_url || it.src).filter(Boolean);
                if (!cancelled) setImages([...urls, ...fallback].slice(0, 16));
            } catch {
                if (!cancelled) setImages(fallback);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="bg-cafe-cream min-h-screen pb-[88px] md:pb-0">
            <SiteNav />
            {stripeMode?.mode === "test" && stripeMode?.payments_available === false && (
                <div className="pt-16 md:pt-20" data-testid="stripe-preview-banner-wrap">
                    <div className="bg-cafe-olive text-cafe-cream text-center py-2.5 px-4 text-[11.5px] md:text-[12.5px]" data-testid="stripe-preview-banner">
                        <span className="font-semibold uppercase tracking-[0.22em] mr-2">Preview</span>
                        {lang === "cn"
                            ? "付款在 Preview 环境暂不可用。配置真实测试密钥后开放购买。"
                            : "Checkout is temporarily disabled in Preview. Purchase will open once a real Stripe test key is configured."}
                    </div>
                </div>
            )}
            <main>
                <CountdownBanner c={c.countdown} lang={lang} />
                <Hero c={c.hero} lang={lang} images={images} kitHref={kitHref} contHref={contHref} />
                <StatsBar stats={c.stats} />
                <Problem c={c.problem} />
                <BeforeAfter c={c.beforeAfter} images={images} />
                <FounderStory c={c.founder} />
                <Pricing c={c.pricing} kitHref={kitHref} contHref={contHref} lang={lang} />
                <WhatsInside c={c.inside} />
                <Showcase c={c.showcase} images={images} />
                <TechStack c={c.tech} />
                <ROI c={c.roi} />
                <Perks c={c.perks} />
                <Testimonials c={c.testi} />
                <FAQ c={c.faq} />
                <Guarantee c={c.guarantee} />
                <FinalCTA c={c.final} kitHref={kitHref} contHref={contHref} lang={lang} />
            </main>
            <SiteFooter />
            <MobileStickyCTA kitHref={kitHref} contHref={contHref} lang={lang} />
        </div>
    );
}
